/**
 * AccessGroupDetailPage — full-page view for managing a single access group.
 *
 * Route: /admin/access-groups/:id
 *
 * Features:
 * - Full parallel-loaded DataLens tree with tri-state checkboxes (Ant Design Tree)
 *   - Backend builds tree via asyncio.gather (2-5 sec for all levels)
 *   - Checkboxes work correctly immediately after loading
 *   - Collections (FolderOutlined), Workbooks (BookOutlined), Dashes (BarChartOutlined)
 *   - checkStrictly=false → automatic indeterminate/tri-state
 * - Diff-based apply: only adds/removes changed entries vs DB state
 * - Search filter across tree nodes (keeps parent path)
 * - Expand all / Collapse all icon buttons
 * - Refresh tree button (busts 15-min backend cache)
 * - Members management tab (unchanged)
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Typography,
  Breadcrumb,
  Table,
  Button,
  Space,
  Tag,
  Tabs,
  Tree,
  Input,
  Popconfirm,
  Modal,
  Select,
  Form,
  Spin,
  Alert,
  Statistic,
  Tooltip,
  message,
  theme,
} from 'antd'
import {
  ArrowLeftOutlined,
  PlusOutlined,
  TeamOutlined,
  AppstoreOutlined,
  CloudSyncOutlined,
  UndoOutlined,
  SearchOutlined,
  ExclamationCircleFilled,
  PlusSquareOutlined,
  MinusSquareOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { TreeProps } from 'antd/es/tree'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'

import { useAuthStore } from '@/store/auth'
import { useQueryClient } from '@tanstack/react-query'
import {
  useAccessGroup,
  useGroupEntries,
  useAddEntry,
  useDeleteEntry,
  useAddMember,
  useRemoveMember,
  useGroupSyncStatus,
} from '@/hooks/useAccessGroups'
import { fetchDatalensTree } from '@/api/datalens'
import { useOrganizations, useUsers } from '@/hooks/useUsers'
import type { AccessGroupMember } from '@/api/accessGroups'
import { isOrgMembershipRoleKey } from '@/utils/accessGroups'
import {
  collectAllKeys,
  convertTreeNodes,
  entryIdFromKey,
  filterTree,
  findTitleByKey,
  keyFromEntryId,
  nodeIcon,
  type TreeNodeData,
} from '@/utils/datalensTree'

dayjs.locale('ru')

const { Title, Text } = Typography

/** Russian pluralization: 1 дашборд, 2 дашборда, 5 дашбордов */
function pluralize(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100
  const lastDigit = abs % 10
  if (abs > 10 && abs < 20) return many
  if (lastDigit === 1) return one
  if (lastDigit >= 2 && lastDigit <= 4) return few
  return many
}

export default function AccessGroupDetailPage() {
  const { id: groupId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const authUser = useAuthStore((s) => s.user)
  const isSuperadmin = authUser?.role === 'superadmin'
  const queryClient = useQueryClient()
  const { token: themeToken } = theme.useToken()

  // ── State ──
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false)
  const [addMemberForm] = Form.useForm()
  const [searchText, setSearchText] = useState('')
  const [checkedKeys, setCheckedKeys] = useState<string[]>([])
  const [initialCheckedKeys, setInitialCheckedKeys] = useState<string[]>([])
  const [applying, setApplying] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])

  // ── Tree state (full parallel load) ──
  const [treeData, setTreeData] = useState<TreeNodeData[]>([])
  const [treeLoading, setTreeLoading] = useState(true)
  const loadedTreeScopeRef = useRef<string | null>(null)

  // ── Data hooks ──
  const { data: groupDetail, isLoading: detailLoading } = useAccessGroup(groupId ?? '')
  const { data: entries, isLoading: entriesLoading } = useGroupEntries(groupId ?? '')
  const { data: syncStatus } = useGroupSyncStatus(groupId ?? '')
  const { data: organizations } = useOrganizations()
  const { data: usersData } = useUsers({ page: 1, page_size: 200 })

  // ── Mutations ──
  const addEntryMutation = useAddEntry()
  const deleteEntryMutation = useDeleteEntry()
  const addMemberMutation = useAddMember()
  const removeMemberMutation = useRemoveMember()

  // ── Load full tree (parallel via backend asyncio.gather) ──
  const loadTree = useCallback(async (bust?: string) => {
    setTreeLoading(true)
    try {
      const resp = await fetchDatalensTree({
        ...(bust ? { bust } : {}),
        ...(groupDetail?.org_id ? { org_id: groupDetail.org_id } : {}),
      })
      const nodes = convertTreeNodes(resp.tree)
      setTreeData(nodes)
      return nodes
    } catch (err) {
      console.error('Failed to load DataLens tree:', err)
      message.error('Ошибка загрузки дерева DataLens')
      return []
    } finally {
      setTreeLoading(false)
    }
  }, [groupDetail?.org_id])

  useEffect(() => {
    if (detailLoading) return
    const scopeKey = groupDetail?.org_id || 'all'
    if (loadedTreeScopeRef.current === scopeKey) return
    loadedTreeScopeRef.current = scopeKey
    loadTree()
  }, [detailLoading, groupDetail?.org_id, loadTree])

  // ── Initialize checked keys from DB entries ──
  useEffect(() => {
    if (entries) {
      const keys = entries.map((e) => keyFromEntryId(e.datalens_entry_id))
      setCheckedKeys(keys)
      setInitialCheckedKeys(keys)
    }
  }, [entries])

  // ── Computed ──
  const orgName = useMemo(() => {
    if (!groupDetail?.org_id || !organizations) return null
    const org = organizations.find((o) => o.id === groupDetail.org_id)
    return org ? `${org.code} — ${org.name}` : groupDetail.org_id
  }, [groupDetail, organizations])
  const isOrgMembershipGroup = useMemo(
    () => isOrgMembershipRoleKey(groupDetail?.zitadel_role_key),
    [groupDetail?.zitadel_role_key],
  )

  // Filtered tree for search
  const displayTreeData = useMemo(() => {
    return filterTree(treeData, searchText)
  }, [treeData, searchText])

  // When searching, expand all matched nodes; collapse on clear
  useEffect(() => {
    if (searchText) {
      const filtered = filterTree(treeData, searchText)
      setExpandedKeys(collectAllKeys(filtered))
    } else {
      setExpandedKeys([])
    }
  }, [searchText, treeData])

  // ── Diff calculation ──
  const diff = useMemo(() => {
    const initialSet = new Set(initialCheckedKeys)
    const currentSet = new Set(checkedKeys)

    // Only count leaf (entry_) keys for diff
    const added = checkedKeys.filter(
      (k) => k.startsWith('entry_') && !initialSet.has(k),
    )
    const removed = initialCheckedKeys.filter(
      (k) => k.startsWith('entry_') && !currentSet.has(k),
    )
    return { added, removed, hasChanges: added.length > 0 || removed.length > 0 }
  }, [checkedKeys, initialCheckedKeys])

  const userOptions = useMemo(() => {
    const members = groupDetail?.members || []
    const memberIds = new Set(members.map((m) => m.user_id))
    return (usersData?.items || [])
      .filter((u) => !memberIds.has(u.id) && u.is_active)
      .map((u) => ({
        value: u.id,
        label: `${u.full_name} (${u.email})`,
      }))
  }, [usersData, groupDetail])

  // ── Handlers ──
  const handleCheck: TreeProps['onCheck'] = (checked) => {
    // When checkStrictly=false (default), checked is string[]
    if (Array.isArray(checked)) {
      setCheckedKeys(checked as string[])
    } else {
      setCheckedKeys((checked as { checked: string[]; halfChecked: string[] }).checked)
    }
  }

  const handleApply = async () => {
    if (!groupId || !diff.hasChanges) return
    setApplying(true)

    try {
      // Delete removed entries
      if (diff.removed.length > 0 && entries) {
        const entryMap = new Map(
          entries.map((e) => [keyFromEntryId(e.datalens_entry_id), e]),
        )
        for (const key of diff.removed) {
          const entry = entryMap.get(key)
          if (entry) {
            await deleteEntryMutation.mutateAsync({
              groupId,
              entryId: entry.id,
            })
          }
        }
      }

      // Add new entries — find titles from loaded tree
      if (diff.added.length > 0) {
        for (const key of diff.added) {
          const dlEntryId = entryIdFromKey(key)
          const title = findTitleByKey(treeData, key)
          await addEntryMutation.mutateAsync({
            groupId,
            data: {
              datalens_entry_id: dlEntryId,
              entry_type: 'dash',
              entry_title: title,
            },
          })
        }
      }

      // Refresh data from server
      await queryClient.invalidateQueries({
        queryKey: ['access-group-entries', groupId],
      })
      message.success(
        `Сохранено: +${diff.added.length} добавлено, -${diff.removed.length} удалено`,
      )
    } catch {
      message.error('Ошибка сохранения изменений')
    } finally {
      setApplying(false)
    }
  }

  const handleCancel = useCallback(() => {
    setCheckedKeys([...initialCheckedKeys])
    setSearchText('')
    message.info('Изменения отменены')
  }, [initialCheckedKeys])

  const handleAddMember = async () => {
    if (!groupId) return
    try {
      const values = await addMemberForm.validateFields()
      await addMemberMutation.mutateAsync({
        groupId,
        userId: values.user_id,
      })
      setAddMemberModalOpen(false)
      addMemberForm.resetFields()
    } catch {
      // validation error
    }
  }

  const handleRemoveMember = (member: AccessGroupMember) => {
    if (!groupId) return
    removeMemberMutation.mutate({ groupId, userId: member.user_id })
  }

  /** Reset backend cache and reload full tree */
  const handleRefreshTree = useCallback(async () => {
    setExpandedKeys([])
    const nodes = await loadTree(String(Date.now()))
    if (nodes.length > 0) {
      message.success('Структура обновлена')
    }
  }, [loadTree])

  /** Expand all non-leaf nodes */
  const handleExpandAll = useCallback(() => {
    setExpandedKeys(collectAllKeys(displayTreeData))
  }, [displayTreeData])

  /** Collapse all nodes */
  const handleCollapseAll = useCallback(() => {
    setExpandedKeys([])
  }, [])

  // ── Loading state ──
  if (!groupId) {
    return <Alert type="error" message="ID группы не указан" />
  }

  if (detailLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!groupDetail) {
    return <Alert type="error" message="Группа не найдена" />
  }

  // ── Members table columns ──
  const memberColumns: ColumnsType<AccessGroupMember> = [
    {
      title: 'ФИО',
      dataIndex: 'user_full_name',
      key: 'user_full_name',
      ellipsis: true,
      render: (v: string | null) => v || '\u2014',
    },
    {
      title: 'Email',
      dataIndex: 'user_email',
      key: 'user_email',
      ellipsis: true,
      render: (v: string | null) => v || '\u2014',
    },
    {
      title: 'Добавлен',
      dataIndex: 'granted_at',
      key: 'granted_at',
      width: 160,
      render: (dt: string) => dayjs(dt).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Действие',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: AccessGroupMember) => (
        <Popconfirm
          title="Удалить сотрудника из группы?"
          onConfirm={() => handleRemoveMember(record)}
          okText="Удалить"
          cancelText="Отмена"
          okButtonProps={{ danger: true }}
        >
          <Button type="link" size="small" danger>
            Удалить
          </Button>
        </Popconfirm>
      ),
    },
  ]

  const totalMembers = groupDetail?.members?.length ?? 0
  const checkedLeafCount = checkedKeys.filter((k) => k.startsWith('entry_')).length

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          {
            title: (
              <a onClick={() => navigate('/admin/access-groups')}>
                Группы доступа
              </a>
            ),
          },
          { title: groupDetail.name },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
        }}
      >
        <div>
          <Space align="center" style={{ marginBottom: 8 }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/admin/access-groups')}
            >
              Назад к списку
            </Button>
          </Space>
          <Title level={3} style={{ margin: 0, marginBottom: 4 }}>
            {groupDetail.name}
          </Title>
          <Space size="middle">
            {orgName && (
              <Text type="secondary">Организация: {orgName}</Text>
            )}
            {isSuperadmin && groupDetail.zitadel_role_key && (
              <Space size={4} wrap>
                <Tag color="purple">Роль: {groupDetail.zitadel_role_key}</Tag>
                {isOrgMembershipGroup && <Tag color="cyan">org-scope</Tag>}
              </Space>
            )}
            {groupDetail.description && (
              <Text type="secondary">{groupDetail.description}</Text>
            )}
          </Space>
        </div>
        {/* Action buttons — always visible in header */}
        <Space>
          <Button
            icon={<UndoOutlined />}
            onClick={handleCancel}
            disabled={isOrgMembershipGroup || !diff.hasChanges}
          >
            Отменить
          </Button>
          <Button
            type="primary"
            icon={<CloudSyncOutlined />}
            loading={applying}
            disabled={isOrgMembershipGroup || !diff.hasChanges}
            onClick={() => {
              Modal.confirm({
                title: 'Подтвердите изменения',
                icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
                content: diff.hasChanges
                  ? `+${diff.added.length} добавлено, -${diff.removed.length} удалено`
                  : 'Нет изменений',
                okText: 'Применить',
                cancelText: 'Отмена',
                okButtonProps: { style: { backgroundColor: '#52c41a', borderColor: '#52c41a' } },
                onOk: handleApply,
              })
            }}
          >
            Применить
          </Button>
        </Space>
      </div>

      {/* Tabs: Dashboards / Members */}
      <Tabs
        defaultActiveKey="entries"
        items={[
          {
            key: 'entries',
            label: (
              <span>
                <AppstoreOutlined /> {checkedLeafCount} {pluralize(checkedLeafCount, 'дашборд', 'дашборда', 'дашбордов')}
              </span>
            ),
            children: (
              <>
                {isOrgMembershipGroup && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Для групп вида mo&lt;код ЛПУ&gt; доступ к дашбордам не назначается"
                    description="Эта группа используется как org-scope marker. Назначайте видимость BI-дашбордов через top-mngmnt, access-fil, access-podr, access-srv, access-otd или через индивидуальные entries."
                  />
                )}

                {/* Search */}
                <Input
                  placeholder="Поиск по загруженным узлам..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  style={{ marginBottom: 16, maxWidth: 400 }}
                  disabled={isOrgMembershipGroup}
                />

                {/* Expand / Collapse buttons */}
                {!isOrgMembershipGroup && !treeLoading && !entriesLoading && displayTreeData.length > 0 && (
                  <Space style={{ marginBottom: 8 }}>
                    <Tooltip title="Развернуть все">
                      <Button
                        size="small"
                        icon={<PlusSquareOutlined />}
                        onClick={handleExpandAll}
                      />
                    </Tooltip>
                    <Tooltip title="Свернуть все">
                      <Button
                        size="small"
                        icon={<MinusSquareOutlined />}
                        onClick={handleCollapseAll}
                      />
                    </Tooltip>
                  </Space>
                )}

                {/* Tree */}
                {isOrgMembershipGroup ? (
                  <Alert
                    type="success"
                    showIcon
                    message="У этой группы нет собственных BI entries"
                    description="Пустое ACL-состояние для mo-группы является корректным."
                  />
                ) : treeLoading || entriesLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin tip="Загрузка дерева дашбордов..." />
                  </div>
                ) : displayTreeData.length === 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    message="Дерево дашбордов пусто"
                    description={
                      searchText
                        ? 'Ничего не найдено по запросу.'
                        : 'Не найдено коллекций и воркбуков в DataLens'
                    }
                  />
                ) : (
                  <Tree
                    checkable
                    showIcon
                    icon={nodeIcon}
                    treeData={displayTreeData}
                    checkedKeys={checkedKeys}
                    onCheck={handleCheck}
                    expandedKeys={expandedKeys}
                    onExpand={(keys) => setExpandedKeys(keys as string[])}
                    height={500}
                    style={{
                      background: themeToken.colorBgContainer,
                      border: `1px solid ${themeToken.colorBorderSecondary}`,
                      borderRadius: themeToken.borderRadiusLG,
                      padding: 12,
                    }}
                  />
                )}

                {/* Diff summary + status */}
                <div
                  style={{
                    background: themeToken.colorBgContainer,
                    border: `1px solid ${themeToken.colorBorderSecondary}`,
                    borderRadius: themeToken.borderRadiusLG,
                    padding: '16px 24px',
                    marginTop: 16,
                  }}
                >
                  <Space size="large" wrap>
                    <Statistic
                      title="Доступно дашбордов"
                      value={checkedLeafCount}
                    />
                    {diff.hasChanges && (
                      <>
                        {diff.added.length > 0 && (
                          <Statistic
                            title="Добавлено"
                            value={`+${diff.added.length}`}
                            valueStyle={{ color: '#52c41a' }}
                          />
                        )}
                        {diff.removed.length > 0 && (
                          <Statistic
                            title="Удалено"
                            value={`-${diff.removed.length}`}
                            valueStyle={{ color: '#ff4d4f' }}
                          />
                        )}
                      </>
                    )}
                    {syncStatus?.last_synced_at && (
                      <Statistic
                        title="Последняя синхронизация"
                        value={dayjs(syncStatus.last_synced_at).format('DD.MM.YYYY HH:mm')}
                      />
                    )}
                  </Space>
                </div>

                {/* Refresh tree structure */}
                <div style={{ marginTop: 12 }}>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={treeLoading}
                    onClick={handleRefreshTree}
                    disabled={isOrgMembershipGroup}
                  >
                    Обновить структуру
                  </Button>
                </div>
              </>
            ),
          },
          {
            key: 'members',
            label: (
              <span>
                <TeamOutlined /> {totalMembers} {pluralize(totalMembers, 'участник', 'участника', 'участников')}
              </span>
            ),
            children: (
              <>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{ marginBottom: 16 }}
                  onClick={() => {
                    addMemberForm.resetFields()
                    setAddMemberModalOpen(true)
                  }}
                >
                  Добавить сотрудника
                </Button>

                <Table
                  dataSource={groupDetail?.members || []}
                  columns={memberColumns}
                  rowKey="user_id"
                  size="middle"
                  pagination={
                    totalMembers > 50
                      ? { pageSize: 50, showSizeChanger: true }
                      : false
                  }
                  locale={{ emptyText: 'Нет сотрудников в группе' }}
                />
              </>
            ),
          },
        ]}
      />

      {/* Modal: Add member */}
      <Modal
        title="Добавить сотрудника в группу"
        open={addMemberModalOpen}
        onOk={handleAddMember}
        onCancel={() => {
          setAddMemberModalOpen(false)
          addMemberForm.resetFields()
        }}
        confirmLoading={addMemberMutation.isPending}
        okText="Добавить"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={addMemberForm} layout="vertical">
          <Form.Item
            name="user_id"
            label="Сотрудник"
            rules={[{ required: true, message: 'Выберите сотрудника' }]}
          >
            <Select
              showSearch
              placeholder="Начните вводить ФИО или email"
              options={userOptions}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent="Нет доступных сотрудников"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
